import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import EmailOutlined from '@mui/icons-material/EmailOutlined'
import SendOutlined from '@mui/icons-material/SendOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { toast } from 'react-toastify'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  createPractitionerInvitation,
  listPractitionerInvitations,
  resendPractitionerInvitation,
  revokePractitionerInvitation,
} from 'src/supportPortal/services/practitionerInvitationsService'

const TYPE_OPTIONS = [
  { id: 'doctor', label: 'Doctor' },
  { id: 'therapist', label: 'Mental Health Professional' },
  { id: 'screener', label: 'Screener' },
  { id: 'psychology-student', label: 'Psychology Student Program' },
]

const ROLE_OPTIONS_BY_TYPE = {
  doctor: [],
  therapist: [
    { id: 'psychologist', label: 'Psychologist' },
    { id: 'psychotherapist', label: 'Psychotherapist' },
    { id: 'counsellor', label: 'Counsellor' },
    { id: 'nutritionist', label: 'Nutritionist' },
  ],
  screener: [],
  'psychology-student': [],
}

const SUBCATEGORY_OPTIONS_BY_ROLE = {
  psychologist: ['clinical', 'counselling', 'educational', 'sport', 'forensic'],
  psychotherapist: ['clinical', 'counselling', 'family', 'group'],
  counsellor: ['couples', 'family', 'career', 'addictions'],
  nutritionist: ['clinical', 'sport', 'pediatric', 'geriatric'],
}

const DISCIPLINE_OPTIONS_BY_TYPE = {
  therapist: [
    { id: 'psychotherapy', label: 'Psychotherapy' },
    { id: 'nutrition-consultation', label: 'Nutrition Consultation' },
  ],
  doctor: [],
  screener: [],
  'psychology-student': [],
}

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'consumed', label: 'Consumed' },
  { id: 'revoked', label: 'Revoked' },
  { id: 'expired', label: 'Expired' },
]

function statusChipColor(status) {
  switch (status) {
    case 'pending':
      return 'default'
    case 'consumed':
      return 'success'
    case 'revoked':
      return 'error'
    case 'expired':
      return 'warning'
    default:
      return 'default'
  }
}

function inviteApplyUrl(token) {
  const base =
    typeof window !== 'undefined' ? `${window.location.origin}` : ''
  return `${base}/apply/i/${token}`
}

// Phase 14q — pre-baked custom-paragraph templates for the Generate Invitation dialog. Picks
// the most-specific copy available given the selected type / discipline / role / sub-category,
// then layers a lightweight sub-category modifier so the paragraph always reads naturally.
//
// The agent can edit the result freely; we only auto-fill when the textarea is empty or still
// shows the previously-generated template (i.e. the agent hasn't touched it yet). That way
// flipping the role doesn't clobber a paragraph the agent has already personalised.
const ROLE_LABELS = {
  psychologist: 'Psychologist',
  psychotherapist: 'Psychotherapist',
  counsellor: 'Counsellor',
  nutritionist: 'Nutritionist',
}
const DISCIPLINE_LABELS = {
  psychotherapy: 'Psychotherapy',
  'nutrition-consultation': 'Nutrition Consultation',
}
const SUBCATEGORY_FLAVORS = {
  clinical:
    "We're seeing strong demand for a clinical track — DSM/ICD-aligned screenings, formal assessments, structured progress notes — and your fit looks great for it.",
  counselling:
    "Counselling-led practitioners do really well on AUMHUM — we'll set you up with structured intake forms, weekly check-in templates and outcome tracking out of the box.",
  educational:
    "We're expanding the educational-psychology side of the platform — school referrals, learning-needs screenings, parent-loop reporting — and would love your help shaping it.",
  sport:
    "Our sport-psychology cohort is growing fast — performance interventions, return-to-play planning, and team workflows are all in scope.",
  forensic:
    "Forensic-track work on AUMHUM is bespoke — court-aligned reporting, longer assessment cycles, and tighter audit trails — we'd love to plug your practice into that.",
  family:
    "Family-systems work fits the AUMHUM model well — multi-participant sessions, shared notes, and shared homework are first-class on the platform.",
  group: 'Group-format sessions are fully supported — cohort scheduling, shared resources and per-member progress notes are baked in.',
  couples:
    'Couples-therapy bookings on AUMHUM are designed for two-participant sessions out of the box — joint scheduling, dual notes, and consent-aware visibility are built in.',
  career:
    "Career-counselling practitioners benefit from AUMHUM's structured plan templates — intake, milestones, and outcome dashboards are ready to use.",
  addictions:
    "Addictions-recovery cohorts on AUMHUM include relapse-aware scheduling, between-session check-ins and tighter reporting — very useful for the work you do.",
  pediatric:
    'Pediatric-nutrition practitioners use AUMHUM for parent-mediated tracking, growth-aware plans, and longer follow-up cycles — all built in.',
  geriatric:
    'Geriatric-nutrition workflows — slower-paced follow-ups, comorbidity-aware plans, family-loop reporting — all map cleanly onto AUMHUM.',
}

function getRoleSpecificCopy(role) {
  switch (role) {
    case 'psychologist':
      return {
        firstLine:
          "we'd love to bring your psychology practice onto AUMHUM. Whether you lean assessment-heavy or therapy-heavy, the platform supports both — formal reports, structured screenings, and session notes are first-class.",
        ctaLine:
          'Once you complete the short application below, our team will review and walk you through profile setup, your first batch of referrals, and any clinic attachments you want to wire up.',
      }
    case 'psychotherapist':
      return {
        firstLine:
          "we'd love to bring your psychotherapy practice onto AUMHUM. The platform's built around what therapists actually need on a busy week — clean session notes, simple scheduling, secure intake, and patient-friendly billing.",
        ctaLine:
          'Once you complete the application below we\'ll get your AUMHUM profile live, attach any clinics you work out of, and surface you to relevant referrals.',
      }
    case 'counsellor':
      return {
        firstLine:
          "we'd love to bring your counselling practice onto AUMHUM. The platform handles the operational pieces — intakes, scheduling, progress notes, billing — so the session itself stays the focus.",
        ctaLine:
          'Once you complete the application below, we\'ll set up your AUMHUM profile and walk you through the templates and referral flow our counsellors use most.',
      }
    case 'nutritionist':
      return {
        firstLine:
          "we'd love to bring your nutrition practice onto AUMHUM. The platform is built for nutrition workflows — structured plans, follow-up reminders, and patient-friendly tracking are all baked in.",
        ctaLine:
          'Once you complete the application below, we\'ll get your AUMHUM profile live and walk you through the plan-template library and patient-loop tools our nutritionists rely on.',
      }
    default:
      return null
  }
}

function getTypeSpecificCopy(type, discipline) {
  if (type === 'doctor') {
    return {
      firstLine:
        "we'd love to bring your medical practice onto AUMHUM. The platform handles the operational side — secure scheduling, e-prescriptions, follow-up loops, and patient-friendly billing — while keeping clinical control firmly with you.",
      ctaLine:
        'Once you complete the short application below, our team will review your registration details, set up your AUMHUM profile, and walk you through the parts of the platform doctors lean on most.',
    }
  }
  if (type === 'therapist') {
    if (discipline === 'nutrition-consultation') {
      return {
        firstLine:
          "we'd love to bring your nutrition consultation practice onto AUMHUM. The platform is built around how nutrition practitioners actually work day-to-day — structured plans, follow-up reminders, and patient-friendly tracking are first-class.",
        ctaLine:
          'Once you complete the application below, our team will set up your AUMHUM profile and walk you through the plan-template library and follow-up tools our nutrition partners rely on.',
      }
    }
    if (discipline === 'psychotherapy') {
      return {
        firstLine:
          "we'd love to bring your psychotherapy practice onto AUMHUM. The platform's built around what mental-health practitioners actually need on a busy week — clean session notes, secure intakes, structured progress tracking, and simple billing.",
        ctaLine:
          'Once you complete the short application below, our team will review your details, set up your AUMHUM profile, and walk you through scheduling, screening flows and your first batch of referrals.',
      }
    }
    return {
      firstLine:
        "we'd love to bring your mental-health practice onto AUMHUM. The platform's built around what practitioners actually need on a busy week — clean session notes, secure intakes, simple scheduling, and patient-friendly billing.",
      ctaLine:
        'Once you complete the short application below, our team will set up your AUMHUM profile and walk you through scheduling, screenings and your first batch of referrals.',
    }
  }
  if (type === 'screener') {
    return {
      firstLine:
        "we'd love to onboard you as a screener on AUMHUM. Screeners on the platform run the structured first-contact session — intake, presenting-concern triage, and a clean handoff to the right practitioner — and that work is what makes the rest of our care flow tick.",
      ctaLine:
        'Once you complete the short application below, our team will review your details, set up your screener profile, and walk you through the screening templates and triage flow.',
    }
  }
  if (type === 'psychology-student') {
    return {
      firstLine:
        "we'd love to have you on the AUMHUM Psychology Student Program. Students on the program shadow live work, run structured screenings under supervision, and contribute to our research and outcome-tracking efforts — all logged towards your training hours.",
      ctaLine:
        'Once you complete the short application below, the program team will review your details, attach a supervisor, and walk you through the student dashboard and observation flow.',
    }
  }
  return null
}

/**
 * Build a fully-baked invitation paragraph based on type / discipline / role / sub-category.
 * The agent can edit the result freely afterwards; the dialog tracks the auto-generated value
 * so re-runs only replace the textarea when the agent hasn't manually edited it.
 */
function buildInvitationParagraph({ type, discipline, role, subCategory, inviteeName }) {
  const greeting = inviteeName?.trim() ? `Hi ${inviteeName.trim()},` : 'Hi there,'

  // Role copy is the most specific — therapist + role wins over therapist + discipline.
  const roleCopy = role ? getRoleSpecificCopy(role) : null
  const typeCopy = !roleCopy ? getTypeSpecificCopy(type, discipline) : null

  if (!roleCopy && !typeCopy) {
    return ''
  }
  const lead = roleCopy?.firstLine || typeCopy?.firstLine
  const cta = roleCopy?.ctaLine || typeCopy?.ctaLine

  const flavor = subCategory && SUBCATEGORY_FLAVORS[subCategory]
  const middle = flavor
    ? `\n\n${flavor}`
    : ''

  const closing =
    "\n\nThe full process takes about 15 minutes. If anything looks off, just reply to this email — we're happy to hop on a quick call.\n\nLooking forward to having you with us,\nTeam AUMHUM"

  return `${greeting}\n\n${lead}${middle}\n\n${cta}${closing}`
}

// Phase 14q — preview helpers. The actual email template lives server-side in
// PractitionerApplicationLifecycleTemplateMigrationService#renderInvitationBody (subject, pre-
// header, sender, body wrapping). We mirror it here for the in-app preview drawer so the agent
// can confirm the email reads correctly with the chosen pre-fill values + custom paragraph
// before they hit Generate.
const SENDER_FROM_EMAIL = 'info@aumhum.com'
const SENDER_FROM_NAME = 'AUMHUM'
const INVITATION_SUBJECT = "You're invited to apply as a practitioner on AUMHUM"
const INVITATION_PREHEADER = (inviteeName) =>
  `${inviteeName?.trim() || 'Friend'}, your invitation to AUMHUM is waiting.`
const DEFAULT_EXPIRY_DAYS = 14

function previewExpiresAt() {
  const exp = new Date(Date.now() + DEFAULT_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
  return exp.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Render the invitation paragraph for HTML preview — convert linebreaks into paragraphs so the
// drawer matches what the recipient will actually see (the server's email template wraps the
// note in a soft-grey blockquote-style block).
function paragraphToHtml(text) {
  if (!text) return ''
  return text
    .split(/\n{2,}/)
    .map((p) => p.replace(/\n/g, '<br/>'))
    .map((p) => `<p style="margin:0 0 12px 0;">${p}</p>`)
    .join('')
}

// Phase 14d listing — render server-side ISO timestamps as friendly local strings. Falls back to
// the raw value when the date can't be parsed so we never blank-out the chip.
function formatDateTime(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Surfaces the "Expires …" chip in warning color when the invitation has under 48 hours left, so
// support agents notice and re-issue if needed.
function isExpiringSoon(expiresAt) {
  if (!expiresAt) return false
  const exp = new Date(expiresAt).getTime()
  if (Number.isNaN(exp)) return false
  const hoursLeft = (exp - Date.now()) / (1000 * 60 * 60)
  return hoursLeft > 0 && hoursLeft <= 48
}

function isExpired(expiresAt) {
  if (!expiresAt) return false
  const exp = new Date(expiresAt).getTime()
  return !Number.isNaN(exp) && exp <= Date.now()
}

export default function PractitionerInvitationsPage() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [list, setList] = useState({ loading: true, data: [], error: '' })
  const [createOpen, setCreateOpen] = useState(false)
  const [resendInvite, setResendInvite] = useState(null)

  async function load() {
    setList((prev) => ({ ...prev, loading: true }))
    const filter = statusFilter === 'all' ? undefined : statusFilter
    const resp = await listPractitionerInvitations({ status: filter })
    if (resp.ok) {
      setList({ loading: false, data: resp.data, error: '' })
    } else {
      setList({ loading: false, data: [], error: resp.message })
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  function handleCopy(token) {
    const url = inviteApplyUrl(token)
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).then(
        () => toast.success('Apply link copied.'),
        () => toast.error('Could not copy. Use the URL manually.'),
      )
    }
  }

  async function handleRevoke(invitation) {
    const reason = window.prompt(
      `Reason to revoke this ${invitation.practitionerType} invitation? (Optional)`,
    )
    if (reason === null) return
    const resp = await revokePractitionerInvitation(invitation.id, reason || '')
    if (resp.ok) {
      toast.success('Invitation revoked.')
      load()
    } else {
      toast.error(resp.message || 'Could not revoke.')
    }
  }

  return (
    <>
      <SupportHeader heading="Practitioner Invitations" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="body1" color="text.secondary">
            Token-gated invitations skip the public Basic-form step and drop applicants directly
            into the rich application. Share the generated link via email or WhatsApp.
          </Typography>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Generate invitation
          </Button>
        </Stack>

        <Tabs
          value={statusFilter}
          onChange={(_e, next) => setStatusFilter(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {STATUS_FILTERS.map((f) => (
            <Tab key={f.id} value={f.id} label={f.label} />
          ))}
        </Tabs>

        {list.loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : list.error ? (
          <Alert severity="error">{list.error}</Alert>
        ) : list.data.length === 0 ? (
          <Alert severity="info">No invitations match the current filter.</Alert>
        ) : (
          <Stack spacing={1.5}>
            {list.data.map((inv) => {
              const typeLabel =
                TYPE_OPTIONS.find((t) => t.id === inv.practitionerType)?.label || inv.practitionerType
              const url = inviteApplyUrl(inv.token)
              return (
                <Paper key={inv.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {typeLabel}
                        </Typography>
                        {inv.role && (
                          <Chip size="small" label={inv.role} variant="outlined" />
                        )}
                        {inv.subCategory && (
                          <Chip size="small" label={inv.subCategory} variant="outlined" />
                        )}
                        {inv.discipline && (
                          <Chip size="small" label={inv.discipline} variant="outlined" />
                        )}
                        <Chip
                          size="small"
                          color={statusChipColor(inv.status)}
                          label={inv.status}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {inv.prefillFullName || 'No prefill name'}
                        {inv.prefillEmail ? ` · ${inv.prefillEmail}` : ''}
                        {inv.prefillMobile ? ` · ${inv.prefillMobile}` : ''}
                      </Typography>
                      {/* Phase 14d listing — surface sender + dispatch + expiry inline. */}
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.75 }}
                      >
                        {inv.invitedByDisplayName && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Sent by ${inv.invitedByDisplayName}`}
                          />
                        )}
                        {(inv.dispatchedAt || inv.createdTime) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Sent on ${formatDateTime(inv.dispatchedAt || inv.createdTime)}`}
                          />
                        )}
                        {inv.dispatchedMediums && inv.dispatchedMediums.length > 0 ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="info"
                            label={`Via ${inv.dispatchedMediums.join(' + ')}`}
                          />
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="No auto-dispatch (link copied manually)"
                          />
                        )}
                        {inv.expiresAt && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={isExpiringSoon(inv.expiresAt) ? 'warning' : 'default'}
                            label={`Expires ${formatDateTime(inv.expiresAt)}`}
                          />
                        )}
                      </Stack>
                      {inv.invitationNote && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                          Custom paragraph: {inv.invitationNote}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}
                      >
                        {url}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Resend invitation">
                        <span>
                          <IconButton
                            disabled={inv.status !== 'pending'}
                            onClick={() => setResendInvite(inv)}
                          >
                            <SendOutlined fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Copy apply link">
                        <span>
                          <IconButton
                            disabled={inv.status !== 'pending'}
                            onClick={() => handleCopy(inv.token)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Revoke">
                        <span>
                          <IconButton
                            disabled={inv.status !== 'pending'}
                            color="error"
                            onClick={() => handleRevoke(inv)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
        )}
      </Box>

      <CreateInvitationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false)
          load()
        }}
      />
      <ResendInvitationDialog
        invitation={resendInvite}
        open={Boolean(resendInvite)}
        onClose={() => setResendInvite(null)}
        onResent={() => {
          setResendInvite(null)
          load()
        }}
      />
    </>
  )
}

function ResendInvitationDialog({ invitation, open, onClose, onResent }) {
  const [resendingKey, setResendingKey] = useState('')
  const url = invitation?.token ? inviteApplyUrl(invitation.token) : ''
  const expired = isExpired(invitation?.expiresAt)
  const hasEmail = Boolean(invitation?.prefillEmail)
  const hasWhatsapp = Boolean(invitation?.prefillMobile)

  useEffect(() => {
    if (!open) setResendingKey('')
  }, [open])

  function copyLink() {
    if (!url) return
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).then(
        () => toast.success('Apply link copied.'),
        () => toast.error('Could not copy. Use the URL manually.'),
      )
    }
  }

  async function resend(mediums, key) {
    if (!invitation?.id || mediums.length === 0) return
    setResendingKey(key)
    const resp = await resendPractitionerInvitation(invitation.id, mediums)
    setResendingKey('')
    if (resp.ok) {
      toast.success('Invitation resent.')
      onResent?.(resp.data)
    } else {
      toast.error(resp.message || 'Could not resend invitation.')
    }
  }

  const remoteDisabled = invitation?.status !== 'pending' || expired
  const options = [
    {
      id: 'email',
      label: 'Email',
      detail: invitation?.prefillEmail || 'No email on this invitation.',
      icon: <EmailOutlined fontSize="small" />,
      mediums: ['email'],
      disabled: remoteDisabled || !hasEmail,
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      detail: invitation?.prefillMobile || 'No mobile on this invitation.',
      icon: <WhatsAppIcon fontSize="small" />,
      mediums: ['whatsapp'],
      disabled: remoteDisabled || !hasWhatsapp,
    },
    {
      id: 'email-whatsapp',
      label: 'Email + WhatsApp',
      detail: hasEmail && hasWhatsapp
        ? `${invitation.prefillEmail} and ${invitation.prefillMobile}`
        : 'Needs both email and mobile.',
      icon: <SendOutlined fontSize="small" />,
      mediums: ['email', 'whatsapp'],
      disabled: remoteDisabled || !hasEmail || !hasWhatsapp,
    },
  ]

  return (
    <Dialog open={open} onClose={resendingKey ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Resend invitation</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {expired ? (
            <Alert severity="warning">
              This invitation expired on {formatDateTime(invitation?.expiresAt)}. Generate a new
              invitation before sending it again.
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Pick how to send the existing apply link. Only channels already saved on the
            invitation can be resent.
          </Typography>
          {options.map((option) => (
            <Button
              key={option.id}
              variant="outlined"
              startIcon={option.icon}
              disabled={option.disabled || Boolean(resendingKey)}
              onClick={() => resend(option.mediums, option.id)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                py: 1.25,
                px: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {resendingKey === option.id ? 'Sending...' : option.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', wordBreak: 'break-word' }}
                >
                  {option.detail}
                </Typography>
              </Box>
            </Button>
          ))}
          <Button
            variant="text"
            startIcon={<ContentCopy fontSize="small" />}
            disabled={!url || Boolean(resendingKey)}
            onClick={copyLink}
            sx={{ justifyContent: 'flex-start' }}
          >
            Copy apply link
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={Boolean(resendingKey)} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function CreateInvitationDialog({ open, onClose, onCreated }) {
  // Phase 14ao — `prefillMobile` carries the full international number with the dial-code
  // prefix but no leading `+` (matches react-phone-input-2's value contract). On submit we
  // serialise to E.164 (`+919999988888`). `prefillCountryCode` is the lowercase 2-letter
  // ISO code (`in`, `us`, ...) — used to seed the phone-input's country-flag dropdown when
  // the agent reopens the dialog and to forward to the backend so the support listing can
  // render the original country picker selection without re-parsing the digits.
  const [form, setForm] = useState({
    practitionerType: '',
    discipline: '',
    role: '',
    subCategory: '',
    targetSpecialitySlugs: [],
    prefillFullName: '',
    prefillEmail: '',
    prefillMobile: '',
    prefillCountryCode: 'in',
    invitationNote: '',
  })
  const [submitting, setSubmitting] = useState(false)
  // Phase 14q — preview drawer for the email that will get dispatched. Opens on top of the
  // dialog so the agent can flip back without losing form state.
  const [previewOpen, setPreviewOpen] = useState(false)
  // Track the most-recent auto-generated paragraph. We only overwrite the textarea on
  // selection changes when its current value matches our last auto-generated copy (i.e. the
  // agent hasn't typed anything custom on top of it yet).
  const lastAutoGeneratedRef = useRef('')

  useEffect(() => {
    if (!open) {
      setForm({
        practitionerType: '',
        discipline: '',
        role: '',
        subCategory: '',
        targetSpecialitySlugs: [],
        prefillFullName: '',
        prefillEmail: '',
        prefillMobile: '',
        prefillCountryCode: 'in',
        invitationNote: '',
      })
      lastAutoGeneratedRef.current = ''
      setPreviewOpen(false)
    }
  }, [open])

  // Phase 14q — auto-generate the invitation paragraph whenever type / discipline / role /
  // sub-category / inviteeName change. We only mutate the textarea when it's empty OR still
  // shows the previous auto-generated value (i.e. the agent hasn't edited it). Once the agent
  // edits, we leave their text alone forever — they can hit Regenerate to opt back in.
  useEffect(() => {
    if (!open) return
    const next = buildInvitationParagraph({
      type: form.practitionerType,
      discipline: form.discipline,
      role: form.role,
      subCategory: form.subCategory,
      inviteeName: form.prefillFullName,
    })
    if (!next) return
    setForm((prev) => {
      const current = prev.invitationNote || ''
      const isUntouched = current === '' || current === lastAutoGeneratedRef.current
      if (!isUntouched) return prev
      lastAutoGeneratedRef.current = next
      return { ...prev, invitationNote: next }
    })
  }, [
    open,
    form.practitionerType,
    form.discipline,
    form.role,
    form.subCategory,
    form.prefillFullName,
  ])

  function handleRegenerateNote() {
    const next = buildInvitationParagraph({
      type: form.practitionerType,
      discipline: form.discipline,
      role: form.role,
      subCategory: form.subCategory,
      inviteeName: form.prefillFullName,
    })
    if (!next) {
      toast.info('Pick a practitioner type / role first to generate a paragraph.')
      return
    }
    lastAutoGeneratedRef.current = next
    setForm((prev) => ({ ...prev, invitationNote: next }))
    toast.success('Custom paragraph regenerated.')
  }

  const roleOptions = ROLE_OPTIONS_BY_TYPE[form.practitionerType] || []
  const disciplineOptions = DISCIPLINE_OPTIONS_BY_TYPE[form.practitionerType] || []
  const subCategoryOptions = SUBCATEGORY_OPTIONS_BY_ROLE[form.role] || []

  // Phase 14ao — derived mobile validation. `prefillMobile` from react-phone-input-2 is
  // the international number sans `+`. We only consider it "filled" once the agent has
  // typed past the dial-code itself (otherwise just opening the dropdown to "India" shows
  // up as "91" with zero subscriber digits, which we treat as empty so the agent can
  // still skip mobile entirely).
  const dialCodeOnly = form.prefillMobile && form.prefillMobile.length <= 4
  const mobileFilled = Boolean(form.prefillMobile) && !dialCodeOnly
  const mobileValid =
    !mobileFilled || isValidPhoneNumber(`+${form.prefillMobile}`)

  async function handleSubmit() {
    if (!form.practitionerType) {
      toast.error('Pick a practitioner type.')
      return
    }
    if (mobileFilled && !mobileValid) {
      toast.error('Enter a valid mobile number or clear the field.')
      return
    }
    setSubmitting(true)
    const resp = await createPractitionerInvitation({
      practitionerType: form.practitionerType,
      discipline: form.discipline || undefined,
      role: form.role || undefined,
      subCategory: form.subCategory || undefined,
      prefillFullName: form.prefillFullName || undefined,
      prefillEmail: form.prefillEmail || undefined,
      // Phase 14ao — backend stores the full E.164 (`+919999988888`) so the WhatsApp
      // dispatch + login-binding paths can use it without re-parsing. We strip the
      // value entirely when the agent skipped the mobile field so the backend's
      // "no contact prefill" no-op fast-path keeps working unchanged.
      prefillMobile: mobileFilled ? `+${form.prefillMobile}` : undefined,
      prefillCountryCode: mobileFilled
        ? (form.prefillCountryCode || '').toLowerCase() || undefined
        : undefined,
      invitationNote: form.invitationNote || undefined,
    })
    setSubmitting(false)
    if (resp.ok) {
      toast.success('Invitation generated.')
      onCreated?.(resp.data)
    } else {
      toast.error(resp.message || 'Could not create invitation.')
    }
  }

  return (
    // Phase 14ay — bumped maxWidth from "sm" (600px) → "md" (900px). The "sm" cap was
    // squeezing the email TextField and the rich react-phone-input-2 picker into a
    // narrow side-by-side row that overflowed the dialog (the PhoneInput's flag-list
    // dropdown panel popped wider than the column it sat in, and the WhatsApp helper
    // text wrapped past the right border). Combined with the vertical stacking below
    // (email above mobile, each at full dialog width) the form now breathes inside
    // the dialog at all viewports.
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          // Cap the dialog so it doesn't balloon past readable widths on a 4K monitor;
          // 720px is generous for a one-column form with full-width inputs.
          maxWidth: 720,
          // Hide horizontal overflow defensively — the PhoneInput's dropdown is
          // explicitly capped below, but if any other future child measures wider than
          // the dialog (e.g. a long invitation-note paste), we'd rather scroll the body
          // than push the chrome.
          overflowX: 'hidden',
        },
      }}
    >
      <DialogTitle>Generate invitation</DialogTitle>
      <DialogContent dividers sx={{ overflowX: 'hidden' }}>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            select
            fullWidth
            label="Practitioner type"
            value={form.practitionerType}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                practitionerType: e.target.value,
                role: '',
                subCategory: '',
                discipline: '',
              }))
            }
          >
            <MenuItem value="">Select type</MenuItem>
            {TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {disciplineOptions.length > 0 && (
            <TextField
              select
              fullWidth
              label="Discipline"
              value={form.discipline}
              onChange={(e) => setForm((prev) => ({ ...prev, discipline: e.target.value }))}
            >
              <MenuItem value="">(any)</MenuItem>
              {disciplineOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {roleOptions.length > 0 && (
            <TextField
              select
              fullWidth
              label="Role"
              value={form.role}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, role: e.target.value, subCategory: '' }))
              }
            >
              <MenuItem value="">(any)</MenuItem>
              {roleOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {subCategoryOptions.length > 0 && (
            <TextField
              select
              fullWidth
              label="Sub-category"
              value={form.subCategory}
              onChange={(e) => setForm((prev) => ({ ...prev, subCategory: e.target.value }))}
            >
              <MenuItem value="">(any)</MenuItem>
              {subCategoryOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            fullWidth
            label="Pre-fill full name (optional)"
            value={form.prefillFullName}
            onChange={(e) => setForm((prev) => ({ ...prev, prefillFullName: e.target.value }))}
          />
          {/* Phase 14ay — email + mobile stacked vertically (was: side-by-side on `sm`+).
              The two fields had wildly different vertical heights once helper text +
              the phone-input's dropdown overlay were factored in, which made the row
              layout look awkward and pushed the dropdown overlay past the dialog
              border. Vertical stacking gives each field the full dialog width, makes
              the "WhatsApp invitation will go here" affordance more readable, and
              eliminates the overflow entirely. */}
          <TextField
            fullWidth
            label="Pre-fill email"
            type="email"
            autoComplete="off"
            value={form.prefillEmail}
            onChange={(e) => setForm((prev) => ({ ...prev, prefillEmail: e.target.value }))}
            helperText="Optional — sends the invitation by email."
          />
          {/* Phase 14ao / 14ay — modernised mobile picker. Wrapper Box uses width:'100%'
              + overflow:'hidden' so the country-list dropdown panel that react-phone-
              input-2 absolutely-positions below the input is clipped to the dialog
              width on the rare path where it tries to render wider (very long English
              country names like "British Indian Ocean Territory" can otherwise push it
              past the chrome). The dropdown's own internal scrollbar still works. */}
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.75rem',
                mb: 0.5,
                ml: 0.25,
              }}
            >
              Pre-fill mobile (with country code)
            </Typography>
            <Box
              sx={{
                width: '100%',
                // Constrain the absolutely-positioned country dropdown to the field's
                // own bounds so it never spills past the dialog. The library renders
                // the panel as a child of `.react-tel-input` (the wrapper this scoping
                // selector targets) so a per-field cap is the right level.
                '& .react-tel-input .country-list': {
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  zIndex: 1500,
                },
                '& .react-tel-input .country-list .country .country-name': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .react-tel-input .country-list .search': {
                  paddingLeft: '8px',
                },
              }}
            >
              <PhoneInput
                country={form.prefillCountryCode || 'in'}
                value={form.prefillMobile}
                onChange={(value, data) => {
                  setForm((prev) => ({
                    ...prev,
                    prefillMobile: value || '',
                    prefillCountryCode:
                      (data && data.countryCode
                        ? data.countryCode
                        : prev.prefillCountryCode) || 'in',
                  }))
                }}
                onCountryChange={(countryCode) => {
                  if (countryCode) {
                    setForm((prev) => ({
                      ...prev,
                      prefillCountryCode: countryCode.toLowerCase(),
                    }))
                  }
                }}
                enableSearch
                countryCodeEditable={false}
                preferredCountries={['in', 'us', 'gb', 'ae', 'sg', 'au', 'ca']}
                inputStyle={{
                  width: '100%',
                  height: '56px',
                  fontSize: '16px',
                  borderRadius: '4px',
                  borderColor: mobileFilled && !mobileValid ? '#d32f2f' : '#c4c4c4',
                  boxSizing: 'border-box',
                }}
                buttonStyle={{
                  borderTopLeftRadius: '4px',
                  borderBottomLeftRadius: '4px',
                  borderColor: mobileFilled && !mobileValid ? '#d32f2f' : '#c4c4c4',
                  boxSizing: 'border-box',
                }}
                containerStyle={{ width: '100%' }}
                dropdownStyle={{ zIndex: 1500 }}
                specialLabel=""
              />
            </Box>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={0.75}
              sx={{ mt: 0.75, ml: 0.25, pr: 0.5 }}
            >
              {mobileFilled && !mobileValid ? (
                <Typography variant="caption" color="error" sx={{ fontSize: '0.72rem' }}>
                  Enter a valid international mobile number.
                </Typography>
              ) : mobileFilled ? (
                <>
                  <WhatsAppIcon
                    sx={{ fontSize: 14, color: '#25D366', flexShrink: 0, mt: '2px' }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.72rem', lineHeight: 1.4 }}
                  >
                    WhatsApp invitation will go to this number. The applicant will be
                    offered this mobile as their default login.
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.72rem', lineHeight: 1.4 }}
                >
                  Optional — when set, the invitation goes via WhatsApp too and the
                  applicant gets this mobile as a login option.
                </Typography>
              )}
            </Stack>
          </Box>
          {/* Phase 14q — pre-baked custom paragraph. We auto-fill on type/discipline/role/sub-
              category change (only when the agent hasn't manually edited the field), and
              expose a Regenerate button so the agent can opt back in after they've typed over
              the auto copy. */}
          <Stack spacing={0.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Custom paragraph in the invitation email
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Regenerate the paragraph from the current type / discipline / role / sub-category selection. Replaces what's currently in the textarea.">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AutoAwesome fontSize="small" />}
                      onClick={handleRegenerateNote}
                      disabled={!form.practitionerType}
                    >
                      Regenerate
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={6}
              placeholder="Pick a practitioner type and role above to auto-fill a starter paragraph. You can edit it freely."
              value={form.invitationNote}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, invitationNote: e.target.value }))
              }
              helperText="Auto-fills based on type / discipline / role / sub-category. Plain text — renders inline in the invitation email above the apply button."
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
              <Button
                size="small"
                startIcon={<VisibilityOutlined fontSize="small" />}
                onClick={() => setPreviewOpen(true)}
                disabled={!form.practitionerType}
              >
                Preview email
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={submitting || (mobileFilled && !mobileValid)}
          onClick={handleSubmit}
        >
          {submitting ? 'Generating…' : 'Generate'}
        </Button>
      </DialogActions>

      {/* Phase 14q — email preview drawer. Mirrors the server-side template (subject, pre-
          header, sender, HTML body) using the agent's current draft so they can verify how
          the dispatched email will read. The actual render is owned by
          PractitionerApplicationLifecycleTemplateMigrationService#renderInvitationBody on the
          backend; this drawer is a faithful client-side mirror for preview purposes. */}
      <EmailPreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        form={form}
      />
    </Dialog>
  )
}

function EmailPreviewDrawer({ open, onClose, form }) {
  const inviteeName = form?.prefillFullName?.trim() || 'there'
  const recipient =
    form?.prefillEmail?.trim() || '<recipient email — fill in above>'
  const recipientName = form?.prefillFullName?.trim() || ''
  const expiresAt = previewExpiresAt()
  const previewApplyUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/apply/i/<token-generated-on-send>`
      : 'https://health.aumhum.xyz/apply/i/<token-generated-on-send>'
  const subject = INVITATION_SUBJECT
  const preheader = INVITATION_PREHEADER(inviteeName)
  const noteHtml = paragraphToHtml(form?.invitationNote || '')

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Phase 14v — the EmailPreviewDrawer renders inside the Generate Invitation Dialog.
      // MUI's default modal z-index puts a later-mounted Drawer at the same layer as its
      // host Dialog, which lets the dialog's backdrop/paper occlude the drawer's right
      // panel. Lifting the drawer one notch above the modal layer (and the dialog backdrop)
      // makes it visible without us having to portal it outside the dialog DOM tree.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      ModalProps={{
        // Match the elevated z-index on the backdrop so the drawer's own dimmer overlay sits
        // above the dialog rather than getting clipped by it.
        sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
      }}
      PaperProps={{ sx: { width: { xs: '100%', md: 720 }, maxWidth: '100%' } }}
    >
      <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Email preview
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Mirrors the dispatched template — sender, subject, preheader and body all match
              the production email. Tokens like the apply URL are filled in on send.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="close">
            <span style={{ fontSize: 24, lineHeight: 1 }}>×</span>
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        {/* Header table — same shape Outlook / Gmail show above an email body. */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack spacing={0.75}>
            <PreviewRow label="From" value={`${SENDER_FROM_NAME} <${SENDER_FROM_EMAIL}>`} />
            <PreviewRow
              label="To"
              value={recipientName ? `${recipientName} <${recipient}>` : recipient}
            />
            <PreviewRow label="Subject" value={subject} bold />
            <PreviewRow
              label="Preheader"
              value={preheader}
              hint="Shown as the snippet text in the recipient's inbox row."
            />
            <PreviewRow
              label="Expires"
              value={`${expiresAt} (≈ ${DEFAULT_EXPIRY_DAYS} days from generation)`}
              hint="Default invitation TTL. Override server-side if you need a different window."
            />
          </Stack>
        </Paper>

        {/* Body — visually approximates the wrapped server template. Brand colours and the
            blockquote-style note block both match the production HTML. */}
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            background: '#fff',
            color: '#1f2937',
          }}
        >
          <Box
            sx={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              lineHeight: 1.6,
              fontSize: 14,
              color: '#1f2937',
            }}
          >
            <Box
              sx={{
                fontSize: 22,
                fontWeight: 600,
                color: '#7c3aed',
                mb: 1.5,
              }}
            >
              You&apos;re invited to AUMHUM
            </Box>
            <Box sx={{ mb: 2 }}>Hi {inviteeName},</Box>
            <Box sx={{ mb: 2 }}>
              You&apos;ve been invited to apply as a practitioner on AUMHUM. Click the button below
              to complete your application — the link expires on <strong>{expiresAt}</strong>.
            </Box>
            <Box sx={{ my: 2 }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 3,
                  py: 1.25,
                  borderRadius: 1.5,
                  background: '#7c3aed',
                  color: '#fff',
                  fontWeight: 600,
                  letterSpacing: 0.2,
                }}
              >
                Open application
              </Box>
            </Box>
            <Box sx={{ mb: 2, fontSize: 13, color: '#6b7280' }}>
              If the button doesn&apos;t work, copy and paste this URL into your browser:
              <br />
              <Box
                component="span"
                sx={{ color: '#7c3aed', wordBreak: 'break-all' }}
              >
                {previewApplyUrl}
              </Box>
            </Box>
            {form?.invitationNote ? (
              <Box
                sx={{
                  px: 2.25,
                  py: 1.75,
                  borderLeft: '3px solid #f59e0b',
                  background: '#f9fafb',
                  borderRadius: 1,
                  my: 2.5,
                  fontSize: 13,
                  color: '#374151',
                  '& p:last-of-type': { mb: 0 },
                }}
                dangerouslySetInnerHTML={{ __html: noteHtml }}
              />
            ) : (
              <Alert severity="info" sx={{ my: 2 }}>
                No custom paragraph yet. Pick a practitioner type / role and we&apos;ll auto-fill a
                starter paragraph here.
              </Alert>
            )}
            <Box sx={{ mt: 3, fontSize: 13, color: '#6b7280' }}>
              Looking forward to having you with us — Team AUMHUM
            </Box>
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          The actual dispatch happens server-side via the{' '}
          <code>practitioner_invitation_sent</code> template. WhatsApp dispatch (when the
          mobile is filled in) uses a shorter, separate template — not previewed here.
        </Typography>
      </Box>
    </Drawer>
  )
}

function PreviewRow({ label, value, hint, bold = false }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: { sm: 96 }, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: bold ? 700 : 400, wordBreak: 'break-word' }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}
